import { useState } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { DataTable } from "@/components/ui/DataTable/index";
import { GeneralContract, Location, MarketZone } from "@/lib/api/generalContract";
import { Trader, Customer } from "@/lib/api/reports";
import { getGeneralContractColumns } from "../shared/GeneralContractColumns";
import {
  GeneralContractModals,
  GeneralContractModalsProps,
} from "../shared/GeneralContractModals";
import { useGeneralContractHandlers } from "../shared/useGeneralContractHandlers";

interface GeneralContractContentProps {
  showTitle?: boolean;
  contracts: GeneralContract[];
  stagedContracts: GeneralContract[];
  locations: Location[];
  marketZones: MarketZone[];
  customers: Customer[];
  traders: Trader[];
}

export function GeneralContractContent({
  showTitle = true,
  contracts,
  stagedContracts,
  locations,
  marketZones,
  customers,
  traders,
}: GeneralContractContentProps) {
  const [tabValue, setTabValue] = useState("unstaged");
  const {
    isArchiving,
    isReviewing,
    reviewModalId,
    setReviewModalId,
    isCreateOpen,
    isUpdateOpen,
    onArchive,
    onDuplicate,
    onDelete,
    onReview,
    confirmReviewPrompt,
    createPopulateWith,
    updatePopulateWith,
    setIsCreateOpen,
    setIsUpdateOpen,
  } = useGeneralContractHandlers();

  const handleRowClick = (row: GeneralContract, _columnId?: string) => {
    setIsUpdateOpen(true);
    updatePopulateWith(row);
  };

  // Columns for unstaged contracts (duplicate + archive buttons)
  const unstagedColumns = getGeneralContractColumns({
    showArchiveButton: true,
    onDuplicate: onDuplicate,
    onArchive: (id: string) => onArchive({ id }),
  });

  // Columns for staged contracts (duplicate + review + delete buttons)
  const stagedColumns = getGeneralContractColumns({
    showReviewButton: true,
    showDeleteButton: true,
    onDuplicate: onDuplicate,
    onReview: (id: string) => confirmReviewPrompt({ id }),
    onDelete: (id: string) => onDelete({ id }),
  });

  return (
    <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
      {showTitle && (
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          General Contracts
        </h1>
      )}
      <div className="flex justify-end gap-4 mb-4">
        <GeneralContractModals
          locations={locations}
          marketZones={marketZones}
          customers={customers}
          traders={traders}
          onDuplicate={onDuplicate}
          onArchive={onArchive}
          onDelete={onDelete}
          onReview={onReview}
          isArchiving={isArchiving}
          isReviewing={isReviewing}
          reviewModalId={reviewModalId}
          setReviewModalId={setReviewModalId}
          createPopulateWith={createPopulateWith}
          updatePopulateWith={updatePopulateWith}
          isCreateOpen={isCreateOpen}
          setIsCreateOpen={setIsCreateOpen}
          isUpdateOpen={isUpdateOpen}
          setIsUpdateOpen={setIsUpdateOpen}
        />
      </div>

      <Tabs value={tabValue} onValueChange={setTabValue} className="w-full">
        <TabsList>
          <TabsTrigger value="unstaged">Contracts</TabsTrigger>
          <TabsTrigger value="staged">Review</TabsTrigger>
        </TabsList>

        <TabsContent value="unstaged">
          <DataTable
            stickyHeaders
            tableClassName="max-h-[calc(100vh-400px)] overflow-y-scroll"
            data={contracts}
            columns={unstagedColumns}
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            enableColumnVisibility
            enableMultiFilter
            defaultCondensed
            virtualizationRowHeight={49}
            virtualizationOverscan={2}
            enableVirtualization
            enableColumnPinning
            enableExport
            exportFilename={() => "general_contracts"}
            defaultColumnVisibility={{ contract_no_nulls: false }}
            defaultSorting={[{ id: "contract_no_nulls", desc: false }, { id: "created_at", desc: true }]}
            filterStyle="select"
            filterMode="internal"
            emptyMessage="No contracts available"
            rowClickable={true}
            onRowClick={handleRowClick}

          />
        </TabsContent>
        <TabsContent value="staged">
          <DataTable
            stickyHeaders
            tableClassName="max-h-[calc(100vh-400px)] overflow-y-scroll"
            data={stagedContracts}
            columns={stagedColumns}
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            enableColumnVisibility
            enableMultiFilter
            defaultCondensed
            enableColumnPinning
            enableExport
            exportFilename={() => "general_staged_contracts"}
            defaultSorting={[{ id: "created_at", desc: true }]}
            filterStyle="select"
            filterMode="internal"
            emptyMessage="No staged contracts for review"
            rowClickable={true}
            onRowClick={handleRowClick}
          />
        </TabsContent>
      </Tabs>
    </section>
  );
}
